import { addressFormat } from '@src/util/format/addressFormat';

export type AttendanceMapCoords = {
  latitude: number;
  longitude: number;
};

export type ResolvedAttendanceAddress = {
  label: string | null;
  mapAddress: AttendanceMapCoords | null;
};

const toFiniteNumber = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const readCoords = (source: Record<string, unknown> | null | undefined): AttendanceMapCoords | null => {
  if (!source) return null;

  const latitude =
    toFiniteNumber(source.latitude) ??
    toFiniteNumber(source.lat) ??
    toFiniteNumber((source.location as Record<string, unknown> | undefined)?.lat);

  const longitude =
    toFiniteNumber(source.longitude) ??
    toFiniteNumber(source.lng) ??
    toFiniteNumber(source.lon) ??
    toFiniteNumber((source.location as Record<string, unknown> | undefined)?.lon);

  const nestedPosition = (source.location as { position?: Record<string, unknown> } | undefined)
    ?.position;
  const nestedLat = latitude ?? toFiniteNumber(nestedPosition?.lat);
  const nestedLon = longitude ?? toFiniteNumber(nestedPosition?.lon);

  if (nestedLat == null || nestedLon == null) return null;
  return { latitude: nestedLat, longitude: nestedLon };
};

/**
 * Normalizes attendance-record `address` (string, work address, or geo payload)
 * into a display label + coords for LocationsMap.
 */
export const resolveAttendanceAddress = (address: unknown): ResolvedAttendanceAddress => {
  if (address == null) {
    return { label: null, mapAddress: null };
  }

  if (typeof address === 'string') {
    const label = address.trim();
    return { label: label || null, mapAddress: null };
  }

  if (typeof address !== 'object') {
    return { label: null, mapAddress: null };
  }

  const record = address as Record<string, unknown>;
  const formatted = addressFormat({
    address: typeof record.address === 'string' ? record.address : null,
    floorOffice: typeof record.floorOffice === 'string' ? record.floorOffice : null,
    commune: typeof record.commune === 'string' ? record.commune : null,
    city: typeof record.city === 'string' ? record.city : null,
    region: typeof record.region === 'string' ? record.region : null,
    zip: typeof record.zip === 'string' ? record.zip : null,
  });

  const country = typeof record.country === 'string' ? record.country.trim() : '';
  const labelParts = [formatted, country].filter(Boolean);
  const label = labelParts.join(', ') || null;

  return {
    label,
    mapAddress: readCoords(record),
  };
};

/** Synthetic exception-only matrix ids are not Firestore attendance records. */
export const isRealAttendanceRecordId = (attendanceRecordId?: string | null): boolean => {
  if (!attendanceRecordId || !attendanceRecordId.trim()) return false;
  return !attendanceRecordId.startsWith('exception:');
};
